import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Plus, Trash2, Save, Image as ImageIcon, GripVertical } from "lucide-react";import { useSiteContent, fetchSiteContent, saveSiteContent, uploadImage, resolveImageUrl } from "@/lib/siteContent";
import { BADGE_STYLES } from "@/components/site/Badge";
import LibraryModal from "@/components/admin/LibraryModal";

export default function ContentEditor({ token }) {
  const { content, loading } = useSiteContent();
  const [active, setActive] = useState("site");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content]);

  if (loading || !draft) return <div className="text-center text-cozy-brown-soft py-10">Loading editor…</div>;

  const updateSection = (key, value) => setDraft({ ...draft, [key]: value });

  const save = async (key) => {
    setSaving(true);
    try {
      await saveSiteContent(token, { [key]: draft[key] });
      toast.success("Saved! Live on the site now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const tabs = [
    { id: "site", label: "Site Settings" },
    { id: "seo", label: "SEO / Meta Tags" },
    { id: "packages", label: "Packages" },
    { id: "themes", label: "Themes" },
    { id: "gallery", label: "Gallery" },
    { id: "testimonials", label: "Testimonials" },
    { id: "faqs", label: "FAQs" },
  ];

  return (
    <div className="space-y-6" data-testid="content-editor">
      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setActive(t.id)} data-testid={`editor-tab-${t.id}`} className={`px-4 py-2 rounded-full text-sm transition ${active === t.id ? "bg-cozy-gold text-white shadow-cozy" : "bg-white text-cozy-brown hover:bg-cozy-beige"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {active === "site" && <SiteSection draft={draft.site} update={(v) => updateSection("site", v)} token={token} onSave={() => save("site")} saving={saving} />}
      {active === "seo" && <SeoSection draft={draft.site} update={(v) => updateSection("site", v)} onSave={() => save("site")} saving={saving} />}
      {active === "packages" && <ListSection items={draft.packages || []} update={(v) => updateSection("packages", v)} token={token} onSave={() => save("packages")} saving={saving} schema={PACKAGE_SCHEMA} emptyItem={() => ({ id: `pkg-${Date.now()}`, name: "New Package", price: 100, teepees: 1, description: "", image: "", featured: false, badge: "", showOnHome: true })} sectionName="package" />}
      {active === "themes" && <ListSection items={draft.themes || []} update={(v) => updateSection("themes", v)} token={token} onSave={() => save("themes")} saving={saving} schema={THEME_SCHEMA} emptyItem={() => ({ id: `theme-${Date.now()}`, name: "New Theme", description: "", image: "", badge: "", showOnHome: true })} sectionName="theme" />}
      {active === "gallery" && <GallerySection items={draft.gallery || []} update={(v) => updateSection("gallery", v)} token={token} onSave={() => save("gallery")} saving={saving} />}
      {active === "testimonials" && <ListSection items={draft.testimonials || []} update={(v) => updateSection("testimonials", v)} token={token} onSave={() => save("testimonials")} saving={saving} schema={TESTIMONIAL_SCHEMA} emptyItem={() => ({ name: "Parent Name", role: "Role", rating: 5, text: "", photo: "" })} sectionName="testimonial" />}
      {active === "faqs" && <ListSection items={draft.faqs || []} update={(v) => updateSection("faqs", v)} token={token} onSave={() => save("faqs")} saving={saving} schema={FAQ_SCHEMA} emptyItem={() => ({ q: "New Question?", a: "" })} sectionName="faq" />}
    </div>
  );
}

// ---------- Schemas ----------
const PACKAGE_SCHEMA = [
  { key: "name", label: "Name", type: "text" },
  { key: "id", label: "Slug (URL-safe id)", type: "text" },
  { key: "teepees", label: "Number of Teepees", type: "number" },
  { key: "price", label: "Price (USD)", type: "number" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "images", label: "Images (upload multiple — visitors swipe with arrows on hover. Single image keeps the zoom-on-hover effect.)", type: "imageList" },
  { key: "featured", label: "Highlight this card (gold border + ring)", type: "boolean" },
  { key: "badge", label: "Badge Overlay Text (e.g. \"Most Popular\", \"Limited Time\", \"Special\", \"New\", \"Best Value\" — leave blank for none)", type: "text" },
  { key: "badgeStyle", label: "Badge Style", type: "select", options: BADGE_STYLES },
  { key: "showOnHome", label: "Show on Home Page (uncheck to keep it only on the dedicated booking flow)", type: "boolean" },
];
const THEME_SCHEMA = [
  { key: "name", label: "Name", type: "text" },
  { key: "id", label: "Slug (URL-safe id)", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "images", label: "Images (upload multiple — arrows appear on hover)", type: "imageList" },
  { key: "badge", label: "Badge Overlay Text (e.g. \"New\", \"Limited Time\" — leave blank for none)", type: "text" },
  { key: "badgeStyle", label: "Badge Style", type: "select", options: BADGE_STYLES },
  { key: "showOnHome", label: "Show on Home Page", type: "boolean" },
];
const TESTIMONIAL_SCHEMA = [
  { key: "name", label: "Name", type: "text" },
  { key: "role", label: "Role (e.g., Mom of two)", type: "text" },
  { key: "rating", label: "Rating (1-5)", type: "number" },
  { key: "text", label: "Review Text", type: "textarea" },
  { key: "photo", label: "Photo", type: "image" },
];
const FAQ_SCHEMA = [
  { key: "q", label: "Question", type: "text" },
  { key: "a", label: "Answer", type: "textarea" },
];

// ---------- SEO section ----------
function SeoSection({ draft, update, onSave, saving }) {
  const pages = [
    { key: "Home", path: "/" },
    { key: "About", path: "/about" },
    { key: "Themes", path: "/themes" },
    { key: "Booking", path: "/booking" },
    { key: "FAQ", path: "/faq" },
    { key: "Contact", path: "/contact" },
  ];
  const seo = draft?.seo || {};
  const updateSeo = (pageKey, fieldKey, value) => {
    const next = { ...seo, [pageKey]: { ...(seo[pageKey] || {}), [fieldKey]: value } };
    update({ ...draft, seo: next });
  };
  const updateSiteName = (v) => update({ ...draft, siteName: v });

  return (
    <div className="space-y-5" data-testid="editor-seo">
      <div className="bg-cozy-pink/30 rounded-2xl p-5 text-sm text-cozy-brown">
        <strong className="font-serif text-base">How this works:</strong> Set the site name once. Each page's browser tab shows
        <em className="font-script text-cozy-gold text-base"> "Page Title | Site Name"</em>. Leave a page's title blank to use a smart default.
      </div>

      <div className="bg-white rounded-3xl p-6 shadow-cozy border border-cozy-gold/15 space-y-4">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">Site Name (used as suffix in every title tag)</span>
          <input
            value={draft?.siteName || "The Cozy Cove"}
            onChange={(e) => updateSiteName(e.target.value)}
            className="mt-2 w-full rounded-2xl border border-cozy-gold/25 bg-cozy-bg px-4 py-2.5 focus:outline-none focus:border-cozy-gold focus:ring-2 focus:ring-cozy-pink transition"
            data-testid="seo-site-name"
          />
        </label>
      </div>

      {pages.map((p) => {
        const pageSeo = seo[p.key] || {};
        const defaultTitle = `${p.key === "Home" ? "Magical Teepee Sleepovers" : p.key}`;
        return (
          <div key={p.key} className="bg-white rounded-3xl p-6 shadow-cozy border border-cozy-gold/15 space-y-4" data-testid={`seo-page-${p.key}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl">{p.key} <span className="text-xs text-cozy-brown-soft font-body uppercase tracking-widest">— {p.path}</span></h3>
            </div>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">Page Title (browser tab)</span>
              <input
                value={pageSeo.title || ""}
                placeholder={`Default: ${defaultTitle}`}
                onChange={(e) => updateSeo(p.key, "title", e.target.value)}
                className="mt-2 w-full rounded-2xl border border-cozy-gold/25 bg-cozy-bg px-4 py-2.5 focus:outline-none focus:border-cozy-gold focus:ring-2 focus:ring-cozy-pink transition"
                data-testid={`seo-title-${p.key}`}
              />
              <span className="text-xs text-cozy-brown-soft mt-1 block">Will render as: <strong>{(pageSeo.title || defaultTitle)} | {draft?.siteName || "The Cozy Cove"}</strong></span>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">Meta Description (Google search snippet, ~150-160 chars ideal)</span>
              <textarea
                rows={2}
                value={pageSeo.description || ""}
                placeholder="A short, enticing description shown in Google search results."
                onChange={(e) => updateSeo(p.key, "description", e.target.value)}
                className="mt-2 w-full rounded-2xl border border-cozy-gold/25 bg-cozy-bg px-4 py-2.5 focus:outline-none focus:border-cozy-gold focus:ring-2 focus:ring-cozy-pink transition"
                data-testid={`seo-desc-${p.key}`}
              />
            </label>
          </div>
        );
      })}
      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

// ---------- Site Settings section (grouped by page) ----------
function SiteSection({ draft, update, token, onSave, saving }) {
  const sections = [
    {
      title: "Home Page — Hero",
      fields: [
        { key: "heroBadge", label: "Hero Badge (small label above title)" },
        { key: "heroScriptWord", label: "Hero Script Word (e.g., \"Magical\")" },
        { key: "heroTitle", label: "Hero Title (e.g., \"Teepee Sleepovers\")" },
        { key: "heroSubtitle", label: "Hero Subtitle (e.g., \"Delivered To Your Door\")" },
        { key: "heroBody", label: "Hero Body Text", type: "textarea" },
        { key: "heroImageUrl", label: "Hero Image", type: "image" },
      ],
    },
    {
      title: "Home Page — About Section (short teaser)",
      fields: [
        { key: "aboutTitle", label: "About Title" },
        { key: "aboutScriptWord", label: "About Script Word" },
        { key: "aboutBody", label: "About Body (homepage teaser — paragraphs separated by blank lines)", type: "textarea" },
        { key: "aboutImageUrl", label: "About Image", type: "image" },
      ],
    },
    {
      title: "Home Page — Final CTA Banner",
      fields: [
        { key: "finalCtaTitle", label: "Final CTA Title", type: "textarea" },
        { key: "finalCtaSubtitle", label: "Final CTA Subtitle" },
      ],
    },
    {
      title: "Home Page — Gallery Layout",
      fields: [
        { key: "galleryColumns", label: "Columns on Desktop (1-6)", type: "number" },
        { key: "galleryRows", label: "Max Rows to show (0 = show all images)", type: "number" },
      ],
    },
    {
      title: "About Page (/about)",
      fields: [
        { key: "aboutPageBody", label: "Full About Story (paragraphs separated by blank lines)", type: "textarea" },
      ],
    },
    {
      title: "Booking Page (/booking)",
      fields: [
        { key: "bookingFormEmbed", label: "Custom Booking Form Embed Code — paste HTML/iframe from 123contactform, Typeform, etc. (leave blank to use built-in form)", type: "textarea" },
      ],
    },
    {
      title: "Contact Page (/contact)",
      fields: [
        { key: "contactFormEmbed", label: "Custom Contact Form Embed Code — paste HTML/iframe (leave blank to use built-in form)", type: "textarea" },
        { key: "showServiceMap", label: "Show Service Area Map on Contact Page", type: "boolean" },
        { key: "serviceMapImageUrl", label: "Custom Map Image (optional — uploads your own map and replaces the default Lynchburg map with radius circles)", type: "image" },
      ],
    },
    {
      title: "Contact Info (shown in Footer & Contact page — leave blank to hide that line)",
      fields: [
        { key: "contactPhone", label: "Phone" },
        { key: "contactEmail", label: "Email" },
        { key: "instagramHandle", label: "Instagram Handle" },
        { key: "facebookHandle", label: "Facebook Handle" },
        { key: "serviceArea", label: "Service Area" },
      ],
    },
    {
      title: "Analytics",
      fields: [
        { key: "googleAnalyticsId", label: "Google Analytics Measurement ID — paste your GA4 ID like \"G-ABC123XYZ\" (or legacy \"UA-XXXX-Y\"). Leave blank to disable. Get yours at analytics.google.com → Admin → Data Streams." },
      ],
    },
    {
      title: "Social Sharing",
      fields: [
        { key: "ogImageUrl", label: "Social Share Image (used on Facebook/Instagram/iMessage link previews)", type: "image" },
      ],
    },
  ];

  return (
    <div className="space-y-6" data-testid="editor-site">
      {sections.map((sec) => (
        <div key={sec.title} className="bg-white rounded-3xl p-6 lg:p-8 shadow-cozy border border-cozy-gold/15">
          <h3 className="font-serif text-xl text-cozy-brown mb-5 pb-3 border-b border-cozy-gold/15">{sec.title}</h3>
          <div className="space-y-5">
            {sec.fields.map((f) => (
              <FieldRenderer key={f.key} field={f} value={draft?.[f.key]} onChange={(v) => update({ ...draft, [f.key]: v })} token={token} />
            ))}
          </div>
        </div>
      ))}
      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

// ---------- Generic list section (packages/themes/testimonials/faqs) ----------
function ListSection({ items, update, token, onSave, saving, schema, emptyItem, sectionName }) {
  const add = () => update([...items, emptyItem()]);
  const remove = (idx) => update(items.filter((_, i) => i !== idx));
  const updateItem = (idx, newItem) => update(items.map((it, i) => (i === idx ? newItem : it)));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    update(arr);
  };

  return (
    <div className="space-y-5" data-testid={`editor-list-${sectionName}`}>
      {items.map((it, idx) => (
        <div key={idx} className="bg-white rounded-3xl p-6 shadow-cozy border border-cozy-gold/15 space-y-4" data-testid={`editor-${sectionName}-item-${idx}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-cozy-brown-soft text-sm">
              <GripVertical className="w-4 h-4" />
              <span className="font-medium">{it.name || it.q || `Item ${idx + 1}`}</span>
            </div>
            <div className="flex gap-1">
              <button onClick={() => move(idx, -1)} className="px-2 py-1 text-xs rounded bg-cozy-beige hover:bg-cozy-pink transition" data-testid={`move-up-${sectionName}-${idx}`}>↑</button>
              <button onClick={() => move(idx, 1)} className="px-2 py-1 text-xs rounded bg-cozy-beige hover:bg-cozy-pink transition" data-testid={`move-down-${sectionName}-${idx}`}>↓</button>
              <button onClick={() => remove(idx)} className="px-2 py-1 text-xs rounded bg-red-50 hover:bg-red-100 text-red-700 transition" data-testid={`delete-${sectionName}-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          {schema.map((f) => (
            <FieldRenderer key={f.key} field={f} value={it[f.key]} onChange={(v) => updateItem(idx, { ...it, [f.key]: v })} token={token} />
          ))}
        </div>
      ))}
      <div className="flex justify-between items-center">
        <button onClick={add} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cozy-pink hover:bg-cozy-pink/70 text-cozy-brown transition" data-testid={`add-${sectionName}`}>
          <Plus className="w-4 h-4" /> Add
        </button>
        <SaveBar onSave={onSave} saving={saving} compact />
      </div>
    </div>
  );
}

// ---------- Gallery section (list of image URLs) ----------
function GallerySection({ items, update, token, onSave, saving }) {
  const fileRef = useRef(null);
  const [libOpen, setLibOpen] = useState(false);
  const addUrl = () => update([...items, ""]);
  const remove = (idx) => update(items.filter((_, i) => i !== idx));
  const updateAt = (idx, v) => update(items.map((it, i) => (i === idx ? v : it)));

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    toast.loading(`Uploading ${files.length} image${files.length > 1 ? "s" : ""}…`, { id: "upload" });
    try {
      const urls = [];
      for (const file of files) {
        const url = await uploadImage(token, file);
        urls.push(url);
      }
      update([...items, ...urls]);
      toast.success(`Uploaded ${urls.length} image${urls.length > 1 ? "s" : ""}`, { id: "upload" });
    } catch {
      toast.error("Upload failed", { id: "upload" });
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-cozy border border-cozy-gold/15 space-y-5" data-testid="editor-gallery">
      <div className="flex items-center gap-3 flex-wrap">
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" data-testid="gallery-file-input" />
        <button onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cozy-gold text-white hover:bg-cozy-gold-dark transition" data-testid="gallery-upload-btn">
          <Upload className="w-4 h-4" /> Upload New
        </button>
        <button onClick={() => setLibOpen(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cozy-pink hover:bg-cozy-pink/70 text-cozy-brown transition">
          <ImageIcon className="w-4 h-4" /> Choose from Library
        </button>
        <button onClick={addUrl} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cozy-beige hover:bg-cozy-pink/40 text-cozy-brown transition" data-testid="gallery-add-url">
          <Plus className="w-4 h-4" /> Add URL
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((url, idx) => (
          <div key={idx} className="relative group rounded-2xl overflow-hidden bg-cozy-beige" data-testid={`gallery-img-${idx}`}>
            {url ? (
              <img src={resolveImageUrl(url)} alt="" className="w-full h-40 object-cover" />
            ) : (
              <div className="w-full h-40 flex items-center justify-center text-cozy-brown-soft"><ImageIcon className="w-8 h-8" /></div>
            )}
            <input value={url} onChange={(e) => updateAt(idx, e.target.value)} placeholder="Image URL" className="w-full text-xs px-2 py-1.5 bg-white border-t border-cozy-gold/15" data-testid={`gallery-url-${idx}`} />
            <button onClick={() => remove(idx)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/95 hover:bg-red-100 flex items-center justify-center shadow-cozy" data-testid={`gallery-delete-${idx}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-600" />
            </button>
          </div>
        ))}
      </div>
      <SaveBar onSave={onSave} saving={saving} />
      <LibraryModal
        open={libOpen}
        onClose={() => setLibOpen(false)}
        onPick={(urls) => update([...items, ...(Array.isArray(urls) ? urls : [urls])])}
        token={token}
        mode="multi"
      />
    </div>
  );
}

// ---------- Field renderer ----------
function FieldRenderer({ field, value, onChange, token }) {
  const cls = "mt-2 w-full rounded-2xl border border-cozy-gold/25 bg-cozy-bg px-4 py-2.5 focus:outline-none focus:border-cozy-gold focus:ring-2 focus:ring-cozy-pink transition";

  if (field.type === "image") {
    return <SingleImageField field={field} value={value} onChange={onChange} token={token} cls={cls} />;
  }
  if (field.type === "textarea") {
    return (
      <label className="block">
        <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">{field.label}</span>
        <textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} />
      </label>
    );
  }
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 rounded accent-cozy-gold" />
        <span className="text-sm text-cozy-brown">{field.label}</span>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <label className="block">
        <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">{field.label}</span>
        <select value={value || (field.options?.[0]?.value || "")} onChange={(e) => onChange(e.target.value)} className={cls}>
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>
    );
  }
  if (field.type === "imageList") {
    return <ImageListField field={field} value={value} onChange={onChange} token={token} cls={cls} />;
  }
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">{field.label}</span>
      <input type={field.type || "text"} value={value ?? ""} onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)} className={cls} />
    </label>
  );
}

function SingleImageField({ field, value, onChange, token, cls }) {
  const [libOpen, setLibOpen] = useState(false);
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">{field.label}</label>
      <div className="mt-2 flex gap-3 items-start">
        {value && (
          <img src={resolveImageUrl(value)} alt="" className="w-24 h-24 rounded-2xl object-cover border border-cozy-gold/25" />
        )}
        <div className="flex-1 space-y-2">
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL or use the buttons below" className={cls} />
          <div className="flex gap-2 flex-wrap">
            <ImageUpload token={token} onUploaded={(url) => onChange(url)} />
            <button type="button" onClick={() => setLibOpen(true)} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cozy-pink hover:bg-cozy-pink/70 text-cozy-brown transition">
              <ImageIcon className="w-3 h-3" /> Choose from Library
            </button>
          </div>
        </div>
      </div>
      <LibraryModal open={libOpen} onClose={() => setLibOpen(false)} onPick={(u) => onChange(u)} token={token} mode="single" />
    </div>
  );
}

function ImageUpload({ token, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(token, file);
      onUploaded(url);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" onChange={handle} className="hidden" />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cozy-beige hover:bg-cozy-pink transition">
        <Upload className="w-3 h-3" /> {busy ? "Uploading…" : "Upload new"}
      </button>
    </>
  );
}

function ImageListField({ field, value, onChange, token, cls }) {
  const list = Array.isArray(value) ? value : [];
  const fileRef = useRef(null);
  const [libOpen, setLibOpen] = useState(false);
  const addUrl = () => onChange([...list, ""]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const updateAt = (i, v) => onChange(list.map((u, idx) => (idx === i ? v : u)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const arr = [...list];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    toast.loading(`Uploading ${files.length}…`, { id: "ulist" });
    try {
      const urls = [];
      for (const f of files) {
        const url = await uploadImage(token, f);
        urls.push(url);
      }
      onChange([...list, ...urls]);
      toast.success(`Uploaded ${urls.length}`, { id: "ulist" });
    } catch { toast.error("Upload failed", { id: "ulist" }); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">{field.label}</label>
      <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
      <div className="mt-2 flex gap-2 mb-3 flex-wrap">
        <button type="button" onClick={() => fileRef.current?.click()} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cozy-gold text-white hover:bg-cozy-gold-dark transition">
          <Upload className="w-3 h-3" /> Upload New
        </button>
        <button type="button" onClick={() => setLibOpen(true)} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cozy-pink hover:bg-cozy-pink/70 text-cozy-brown transition">
          <ImageIcon className="w-3 h-3" /> Choose from Library
        </button>
        <button type="button" onClick={addUrl} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cozy-beige hover:bg-cozy-pink/40 transition">
          <Plus className="w-3 h-3" /> Add URL
        </button>
      </div>
      {list.length === 0 ? (
        <p className="text-xs text-cozy-brown-soft italic">No images yet. Upload to get started — first image is shown by default; visitors hover to see more.</p>
      ) : (
        <div className="space-y-2">
          {list.map((url, i) => (
            <div key={i} className="flex items-start gap-2 bg-cozy-bg rounded-2xl p-2 border border-cozy-gold/15">
              {url ? (
                <img src={url.startsWith("/api/uploads/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} alt="" className="w-16 h-16 rounded-xl object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-cozy-beige" />
              )}
              <input value={url} onChange={(e) => updateAt(i, e.target.value)} placeholder="Image URL" className="flex-1 rounded-xl border border-cozy-gold/25 bg-white px-3 py-2 text-xs focus:outline-none focus:border-cozy-gold" />
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => move(i, -1)} className="text-[10px] px-2 py-0.5 rounded bg-white hover:bg-cozy-pink/40 transition">↑</button>
                <button type="button" onClick={() => move(i, 1)} className="text-[10px] px-2 py-0.5 rounded bg-white hover:bg-cozy-pink/40 transition">↓</button>
              </div>
              <button type="button" onClick={() => remove(i)} className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      <LibraryModal
        open={libOpen}
        onClose={() => setLibOpen(false)}
        onPick={(urls) => onChange([...list, ...(Array.isArray(urls) ? urls : [urls])])}
        token={token}
        mode="multi"
      />
    </div>
  );
}

function SaveBar({ onSave, saving, compact }) {
  return (
    <div className={compact ? "" : "flex justify-end pt-3 border-t border-cozy-gold/15"}>
      <button onClick={onSave} disabled={saving} className="flex items-center gap-2 px-7 py-3 rounded-full bg-cozy-gold text-white hover:bg-cozy-gold-dark transition shadow-cozy disabled:opacity-60" data-testid="editor-save-btn">
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
      </button>
    </div>
  );
}

// Re-trigger initial fetch when token changes (admin login)
export function refreshSiteContent() { fetchSiteContent(); }
